import { useBrowseCurrentPlanEntitlementsApi } from '@aibees/shared/api/pricing';
import { PlanCapability } from '@aibees/shared/definition';
import { useCurrentSubscriptionApi } from '@aibees/shared/features/price-and-plan/api/use-current-subscription-api';
import { useMemo } from 'react';
import { useActiveAndPendingMembersCount } from './use-active-and-pending-members-count';

/** `limits` sends this instead of a number when the capability is uncapped. */
const UNLIMITED = -1;

export function useCheckLimitReached() {
  const entitlements = useBrowseCurrentPlanEntitlementsApi();
  const subscription = useCurrentSubscriptionApi();
  const { activeAndPendingMembersCount } = useActiveAndPendingMembersCount();

  const seatLimit = entitlements.data?.limits?.[PlanCapability.SEATS];

  // Entitlements are the source of truth; the plan metadata is a fallback for
  // workspaces the entitlements endpoint hasn't been rolled out to yet.
  const seat = useMemo(() => {
    if (seatLimit === UNLIMITED) return undefined;
    if (typeof seatLimit === 'number') return seatLimit;

    // `metadata.seat` is a nullable string and `Number(null)` is 0, so the
    // empty cases have to be rejected before parsing — otherwise an unset
    // value reads as a zero-seat plan.
    const rawSeat = subscription.data?.current?.plan?.metadata.seat;
    if (!rawSeat) return undefined;

    const parsedSeat = Number(rawSeat);
    return Number.isFinite(parsedSeat) ? parsedSeat : undefined;
  }, [seatLimit, subscription.data]);

  // `isInitialLoading` rather than `isLoading`: both queries are gated on
  // `enabled`, and a disabled react-query v4 query reports `isLoading` forever.
  const isSeatLoading =
    entitlements.isInitialLoading ||
    (seatLimit === undefined && subscription.isInitialLoading);

  // `seat` of 0 is a real limit, so compare against undefined rather than
  // relying on truthiness.
  const hasLimitReached = useMemo(
    () => (seat === undefined ? false : activeAndPendingMembersCount >= seat),
    [seat, activeAndPendingMembersCount]
  );

  return { hasLimitReached, seat, isSeatLoading };
}
